import { useMemo, useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserFormValues, userCreateSchema, userUpdateSchema } from '@/api/types';
import { User } from '@/types/user';

type UserFormMode = 'create' | 'update';

interface UseUserFormOptions {
  mode: UserFormMode;
  user?: User | null;
}

interface UseUserFormReturn {
  form: UseFormReturn<UserFormValues>;
  handleSave: () => void;
}

export function useUserForm({ mode, user }: UseUserFormOptions): UseUserFormReturn {
  const defaultValues = useMemo<UserFormValues>(() => {
    if (mode === 'create') {
      return {
        firstName: '',
        lastName: '',
        username: '',
        phone: '',
        email: '',
        password: '',
        confirmPassword: '',
        roles: ['ROLE_USER'],
        state: 1,
      };
    }

    // Edit mode - extract from user
    if (!user) {
      return {
        firstName: '',
        lastName: '',
        username: '',
        phone: '',
        email: '',
        password: '',
        confirmPassword: '',
        roles: ['ROLE_USER'],
        state: 1,
      };
    }

    // Extract roles from userResponse - check roles first (from API response), then authoritySet, then role field
    let rolesValue: string[] = ['ROLE_USER'];
    const userWithRoles = user as any;
    if (
      userWithRoles?.roles &&
      Array.isArray(userWithRoles.roles) &&
      userWithRoles.roles.length > 0
    ) {
      rolesValue = userWithRoles.roles;
    } else if (user.authoritySet && user.authoritySet.length > 0) {
      rolesValue = user.authoritySet;
    } else if (user.role) {
      rolesValue = [user.role];
    }

    // Map enabled field to state: enabled (boolean) -> state (0/1)
    // Check enabled first, then fallback to state field, then default to 1
    let stateValue = 1;
    if (userWithRoles?.enabled !== undefined) {
      stateValue = userWithRoles.enabled === true ? 1 : 0;
    } else if (user.state !== null && user.state !== undefined) {
      stateValue = user.state;
    }

    return {
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      username: user.username ?? '',
      email: user.email ?? '',
      phone: user.phone ?? '',
      password: undefined,
      confirmPassword: undefined,
      roles: rolesValue,
      state: stateValue,
    };
  }, [mode, user]);

  const form = useForm<UserFormValues>({
    resolver: zodResolver(mode === 'create' ? userCreateSchema : userUpdateSchema),
    mode: 'onChange',
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const handleSave = () => {
    // Trigger form submission by finding the form element and calling requestSubmit
    // This will trigger the form's onSubmit handler in UserForm component
    const formElement = document.getElementById('user-form') as HTMLFormElement;
    if (formElement) {
      formElement.requestSubmit();
    }
  };

  return {
    form,
    handleSave,
  };
}
